import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from "react";

/**
 * `next-themes` replaced by ~40 lines (017 T020, research R11).
 *
 * `next-themes` exists to survive a server render and the hydration mismatch that follows — the
 * server does not know the viewer's stored preference, so the first paint is wrong and has to be
 * corrected without flashing. `apps/admin` is a client-only SPA. There is no server render, so
 * none of that machinery has anything to do.
 *
 * What is actually needed is what the template's Tailwind config reads:
 * `darkMode: ["class", '[data-theme="dark"]']` — an attribute on `<html>`. So: write the
 * attribute, remember the choice, and default to the OS setting when there is no choice yet.
 *
 * `useTheme()` keeps `next-themes`' call signature (`{ theme, setTheme }`) so the copied files
 * that consume it needed only their import line changed, not their bodies (FR-004).
 */

export type Theme = "light" | "dark";

const STORAGE_KEY = "steelyard-theme";

function systemTheme(): Theme {
  return typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function storedTheme(): Theme | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === "light" || stored === "dark" ? stored : null;
  } catch {
    // Private mode, or site data blocked. A theme preference is not worth an exception.
    return null;
  }
}

type ThemeContextValue = {
  theme: Theme;
  /**
   * `next-themes` distinguishes `theme` (which may be the literal "system") from `resolvedTheme`
   * (always "light" or "dark"). This provider never stores "system" — the OS preference is read
   * once as the initial value and then resolved — so the two are the same value here. Exposed
   * under both names so the copied call sites needed no edit.
   */
  resolvedTheme: Theme;
  setTheme: (theme: Theme) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({
  children,
  defaultTheme = "light",
}: React.PropsWithChildren<{ defaultTheme?: string }>) {
  // `defaultTheme` is typed `string` because the copied `providers/index.tsx` passes one through
  // from its own caller. Anything that is not a theme name falls back to light rather than
  // writing an attribute the Tailwind config does not recognise.
  const fallback: Theme = defaultTheme === "dark" ? "dark" : "light";
  const [theme, setThemeState] = useState<Theme>(() => storedTheme() ?? fallback);

  useLayoutEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // The attribute is still set; only the memory of it is lost.
    }
  }, []);

  const value = useMemo(() => ({ theme, resolvedTheme: theme, setTheme }), [theme, setTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (context === null) {
    // Outside a provider the honest answer is the OS setting, not a silent "light".
    const theme = systemTheme();
    return { theme, resolvedTheme: theme, setTheme: () => undefined };
  }
  return context;
}
